import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import {
  rentFieldConfiguration,
  rentInitialValues,
  makeFieldConfigDynamic,
} from "../../../formConfigs/Fleet/rentFieldConfig";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import TruckPopup, { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";
import TrailerPopup from "../TrailerPopup/TrailerPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { fetchAllDrivers } from "../IncidentPopup/IncidentPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import CompanyPopup from "../CompanyPopup/CompanyPopup";
import DriverPopup from "../DriverPopup/DriverPopup";
import { useMsal } from "@azure/msal-react";

export const fetchAssets = async (
  selectedAssetType: string,
  token: string | null | undefined,
) => {
  const endpoint =
    selectedAssetType === "Truck" ? "getAllTrucks" : "getAllTrailers";
  const response = await axios.get(`${apiVariable}/${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return response.data.map((item: any) => ({
    label: `${item.unitNumber} | ${item.make} ${item.model} ${item.year}`,
    id: item.id.toString(),
  }));
};

const RentPopup = () => {
  const [open, setOpen] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState<string>("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validatedAsset, setValidatedAsset] = useState<{
    type: string;
    id: string | null;
  } | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const user = useMsal();
  const userName = user.accounts[0].name;

  const {
    data: assetData,
    isError,
    error,
  } = useQuery({
    queryKey: ["assets", selectedAssetType],
    queryFn: () => fetchAssets(selectedAssetType, token),
    enabled: !!selectedAssetType,
  });

  const { data: ownerCompanies } = useQuery({
    queryKey: ["onwerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: !!open,
  });

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: !!token,
  });

  useEffect(() => {
    if (isError && error) {
      setStatus("error");
      setErrorMessage(
        (error as any).response?.data || "Failed to fetch assets",
      );
    }
  }, [isError, error]);

  const rentMutation = useMutation({
    mutationFn: (data: any) => {
      return axios.post(`${apiVariable}/rent`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["rents"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit rent");
    },
  });

  const emptyToNull = (obj: Record<string, any>) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v === "" ? null : v]),
    );

  const handleOpen = () => {
    setValidatedAsset(null);
    setStatus("idle");
    setErrorMessage(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setValidatedAsset(null);
  };

  const validateRentSelection = async (
    assetType: string,
    assetId: string | null,
  ) => {
    if (!assetType || !assetId) {
      setValidatedAsset(null);
      return;
    }

    if (
      validatedAsset &&
      validatedAsset.type === assetType &&
      validatedAsset.id === assetId
    ) {
      return;
    }

    try {
      await axios.get(`${apiVariable}/validateRent`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          truckId: assetType === "Truck" ? assetId : null,
          trailerId: assetType === "Trailer" ? assetId : null,
        },
      });
      setValidatedAsset({ type: assetType, id: assetId });
      setErrorMessage(null);
      setStatus((prevStatus) =>
        prevStatus === "success" ? prevStatus : "idle",
      );
    } catch (error: any) {
      setValidatedAsset(null);
      setStatus("error");
      setErrorMessage(
        error.response?.data ||
          "Failed to validate rent for the selected asset",
      );
    }
  };

  const submitHandler = (data: any) => {
    const cleaned = emptyToNull(data);

    const submitData = {
      ...cleaned,
      truckId:
        selectedAssetType === "Truck" ? getId(cleaned.assetNumber) : null,
      trailerId:
        selectedAssetType === "Trailer" ? getId(cleaned.assetNumber) : null,
      billToId: getId(cleaned.billToId),
      soldToId: cleaned.soldToId ? getId(cleaned.soldToId) : null,
      authorityCompanyId: getId(cleaned.authorityCompanyId),
      driverId: getId(cleaned.driverId),
      addedBy: userName,
      startMileage:
        cleaned.startMileage == null ? null : Number(cleaned.startMileage),
      endMileage:
        cleaned.endMileage == null ? null : Number(cleaned.endMileage),
    };

    delete cleaned.assetAffected;
    delete cleaned.assetNumber;

    rentMutation.mutate(submitData);
  };

  const onValuesChange = (
    values: any,
    setFieldValue?: (field: string, value: any) => void,
  ) => {
    const assetType = values.assetAffected;
    setSelectedAssetType(assetType);

    const assetId = values.assetNumber ? getId(values.assetNumber) : null;
    if (!assetType || !assetId) {
      setValidatedAsset(null);
    } else {
      validateRentSelection(assetType, assetId);
    }

    // Calculate mileage caps if all required values are present and valid
    if (values.mileageRate) {
      const startMileage = values.startMileage
        ? Number(values.startMileage)
        : 0;
      const endMileage = values.endMileage ? Number(values.endMileage) : 0;
      const mileageRate = Number(values.mileageRate);

      if (!isNaN(startMileage) && !isNaN(endMileage) && !isNaN(mileageRate)) {
        const calculatedCaps = (endMileage - startMileage) * mileageRate;
        // Only update if the value has changed to prevent infinite loop
        if (setFieldValue && calculatedCaps !== values.mileageCaps) {
          setFieldValue("mileageCaps", calculatedCaps);
        }
      }
    }
  };

  const dynamicFields = makeFieldConfigDynamic(
    rentFieldConfiguration,
    setSelectedAssetType,
    selectedAssetType,
  );

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Create Rent
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={rentInitialValues}
        title="Rent Form"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        autocompleteData={{
          assets: assetData,
          billTos: ownerCompanies,
          soldTos: ownerCompanies,
          drivers: allDrivers,
          authorityCompanies: ownerCompanies,
        }}
        loading={rentMutation.isPending}
        onValuesChange={onValuesChange}
        fieldComponents={{
          assetNumber: (
            <Box display="flex" justifyContent="center" alignItems="center">
              {selectedAssetType === "Trailer" ? (
                <TrailerPopup />
              ) : (
                <TruckPopup />
              )}
            </Box>
          ),
          authorityCompanyId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CompanyPopup />
            </Box>
          ),
          billToId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CompanyPopup />
            </Box>
          ),
          soldToId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CompanyPopup />
            </Box>
          ),
          driverId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <DriverPopup />
            </Box>
          ),
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage="Rent added successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default RentPopup;
